"use client"

import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from "@/components/ui/dropdown-menu"
import { CustomFilterIcon } from "@/components/custom-filter-icon"
import { CustomColumnsIcon } from '@/components/custom-columns-icon'
import type { WorkflowStage } from "@/types/workflow"
import { useTranslation } from '@/hooks/use-translation'
import React from "react"
import { Pin, ChevronDown, ScanSearch, RotateCw, Download } from 'lucide-react'

interface GridHeaderProps {
  stage: WorkflowStage
  stageConfig?: any // Add stage configuration data
  language?: string // Add language parameter for localization
  totalRecords: number
  selectedCount: number
  hasActiveFilters: boolean
  onEditColumns: () => void
  onToggleFilter: () => void
  onClearFilters: () => void
  onResetToCurrentView?: () => void
  onResetToDefault?: () => void
  currentViewName?: string
  isDefaultView?: boolean
  onDeselectAll: () => void
  showFilterButton?: boolean
  filterCount?: number
  isFilterActive?: boolean
  isLoadingHeader?: boolean
  onBulkEdit?: () => void
  onMoveTo?: () => void // <-- add this
  /**
   * Mark read / unread for the current selection. Rendered only when
   * {@link canMarkMessages} — the same rule that decides whether the Message column shows a
   * trigger, so the menu never offers an action that would silently do nothing.
   */
  onMarkMessages?: (read: boolean) => void
  canMarkMessages?: boolean
  isMarkingMessages?: boolean
  /**
   * Mute (false) or unmute (true) the selection — whether these threads notify the caller from now on.
   * Separate from onMarkMessages because it is a different act, not another flag on the same one:
   * read state is about what already arrived, muting is about what arrives next.
   */
  onMuteMessages?: (subscribed: boolean) => void
  isMutingMessages?: boolean
  // Optional pin dock to support drag-drop pinning/unpinning
  showPinDock?: boolean
  onPinDockDrop?: () => void
  onPinDockClick?: () => void
  showEditColumnsButton?: boolean
  /** Opens the Jump-to-Column command palette (same action as Cmd/Ctrl+K). */
  onJumpToColumn?: () => void
  extraControls?: React.ReactNode
  /**
   * Renders BEFORE Edit columns, alongside the pin dock / jump-to-column tiles.
   * Used by the VMS tab to put the Settings circular icon button at the very
   * front of the right-side action group.
   */
  leadingControls?: React.ReactNode
  /**
   * Current state of the Show-Completed toggle and the handler to flip it.
   * The toggle only renders when <c>stageConfig?.showCompletedToggle</c> is
   * truthy — same gate the old below-header rendering used — so stages that
   * haven't opted in are unaffected. Lives inside GridHeader (not as a
   * separate row below the header) so the toggle visually aligns with
   * Edit columns / Filter, matching the ATS / Client Request layout.
   */
  showCompleted?: boolean
  onShowCompletedChange?: (checked: boolean) => void
  /**
   * Replaces the default title (e.g. "Onboarding (108)") on the left side of
   * the header. Used by the VMS tab to inline a Requests/Emails selector so
   * the page doesn't need a separate toolbar above the grid. The total-records
   * count is suppressed when this is provided — owner of the slot decides
   * whether to render its own count.
   */
  titleSlot?: React.ReactNode
  /**
   * Refresh handler — re-runs the current search query (same filter, sort,
   * page, facets) without refetching configurations / views / assigned-users.
   * Renders a small icon button next to the title. Omitted = button hidden.
   * Spins while <see cref="isLoadingHeader"/> is true so a user-triggered
   * refresh shows visible progress instead of looking like a no-op.
   */
  onRefresh?: () => void
  /**
   * Optional inline chip rendered just after the refresh button in the
   * title cluster. Currently driven by `temporaryQueryFilter` in
   * workflow-grid (e.g. the VmsEmail → linked VmsRequest "Showing: …"
   * pill). Lives in the header rather than as a separate row below so
   * vertical space is preserved and the indicator visually associates
   * with the title/totals it's narrowing.
   */
  filterIndicator?: React.ReactNode
  /**
   * Opens the workflow-hub data-export modal. When provided AND
   * <see cref="showExportButton"/> is true the Export button renders right
   * after Filter in the right-side action group. Hidden when the user lacks
   * the ExportData authorized-action code — the gate is owned by the host
   * (workflow-grid passes false through showExportButton); this slot just
   * fires the open handler.
   */
  onExport?: () => void
  /** Render the Export button. Falsy = hidden entirely. */
  showExportButton?: boolean
}

export const GridHeader = React.forwardRef<HTMLDivElement, GridHeaderProps>(function GridHeader({
  stage,
  stageConfig,
  language = 'en',
  totalRecords,
  selectedCount,
  hasActiveFilters,
  onEditColumns,
  onToggleFilter,
  onClearFilters,
  onResetToCurrentView,
  onResetToDefault,
  currentViewName,
  isDefaultView = false,
  onDeselectAll,
  showFilterButton = true,
  filterCount = 0,
  isFilterActive = false,
  isLoadingHeader = false,
  onBulkEdit,
  onMoveTo,
  onMarkMessages,
  canMarkMessages = false,
  isMarkingMessages = false,
  onMuteMessages,
  isMutingMessages = false,
  showPinDock,
  onPinDockDrop,
  onPinDockClick,
  showEditColumnsButton = true,
  onJumpToColumn,
  extraControls,
  leadingControls,
  showCompleted = false,
  onShowCompletedChange,
  titleSlot,
  onRefresh,
  filterIndicator,
  onExport,
  showExportButton = false,
}: GridHeaderProps, ref) {
  const { t } = useTranslation();

  return (
    <div
      ref={ref}
      style={{
        background: '#F9F9FF',
        borderRadius: 16,
        padding: '18px 32px',
        marginBottom: 24,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxShadow: '0 1px 4px 0 rgba(0,0,0,0.02)',
        border: 'solid #E7E9ED 1px',
        minHeight: 80,
      }}
    >
      {selectedCount > 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ color: '#0F214B', fontWeight: 400, fontSize: 16 }}>
            {selectedCount} {t('itemsSelected')}
          </span>
          <button
            onClick={onDeselectAll}
            style={{
              background: 'none',
              border: 'none',
              color: '#0F214B',
              fontSize: 18,
              cursor: 'pointer',
              marginRight: 4,
            }}
            aria-label={t('clearSelection')}
          >
            ×
          </button>
          {/* Dropdown menu for actions */}
          <div style={{ minWidth: 140 }}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  style={{
                    border: '1px solid #E0E3EB',
                    borderRadius: 12,
                    padding: '8px 18px',
                    fontSize: 16,
                    background: '#fff',
                    color: '#2B345E',
                    fontWeight: 400,
                    outline: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    minWidth: 140,
                  }}
                >
                  {t('selectAction') || 'Select action'}
                  <ChevronDown size={16} style={{ marginLeft: 8, opacity: 0.7 }} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="start" 
                style={{ 
                  minWidth: 140,
                  background: '#fff',
                  border: '1px solid #E0E3EB',
                  borderRadius: 8,
                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                }}
              >
                <DropdownMenuItem 
                  onClick={() => onBulkEdit && onBulkEdit()}
                  style={{
                    fontSize: 16,
                    color: '#2B345E',
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                >
                  {t('edit') || 'Edit'}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => onMoveTo && onMoveTo()}
                  style={{
                    fontSize: 16,
                    color: '#2B345E', 
                    padding: '8px 12px',
                    cursor: 'pointer'
                  }}
                >
                  {t('bulkAction') || 'Bulk action'}
                </DropdownMenuItem>
                {/* One "Message" entry rather than four siblings. The read pair and the mute pair are
                    the same subject and read as a group; flat, they crowded Edit and Bulk action and
                    the menu stopped having an obvious shape. */}
                {canMarkMessages && (onMarkMessages || onMuteMessages) && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger
                      style={{ fontSize: 16, color: '#2B345E', padding: '8px 12px', cursor: 'pointer' }}
                    >
                      {t('message') || 'Message'}
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent
                      style={{
                        minWidth: 170,
                        background: '#fff',
                        border: '1px solid #E0E3EB',
                        borderRadius: 8,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                      }}
                    >
                      {onMarkMessages && (
                        <>
                          <DropdownMenuItem
                            disabled={isMarkingMessages}
                            onClick={() => onMarkMessages(true)}
                            style={{ fontSize: 16, color: '#2B345E', padding: '8px 12px', cursor: 'pointer' }}
                          >
                            {t('markAsRead') || 'Mark as read'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={isMarkingMessages}
                            onClick={() => onMarkMessages(false)}
                            style={{ fontSize: 16, color: '#2B345E', padding: '8px 12px', cursor: 'pointer' }}
                          >
                            {t('markAsUnread') || 'Mark as unread'}
                          </DropdownMenuItem>
                        </>
                      )}
                      {/* Separated because these two are a different act: read state is about what has
                          already arrived, muting is about what arrives next. */}
                      {onMarkMessages && onMuteMessages && <DropdownMenuSeparator />}
                      {onMuteMessages && (
                        <>
                          <DropdownMenuItem
                            disabled={isMutingMessages}
                            onClick={() => onMuteMessages(false)}
                            style={{ fontSize: 16, color: '#2B345E', padding: '8px 12px', cursor: 'pointer' }}
                          >
                            {t('mute') || 'Mute'}
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            disabled={isMutingMessages}
                            onClick={() => onMuteMessages(true)}
                            style={{ fontSize: 16, color: '#2B345E', padding: '8px 12px', cursor: 'pointer' }}
                          >
                            {t('unmute') || 'Unmute'}
                          </DropdownMenuItem>
                        </>
                      )}
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {isLoadingHeader ? (
            <span
              className="animate-pulse"
              style={{
                display: 'inline-block',
                width: 180,
                height: 28,
                borderRadius: 6,
                background: 'linear-gradient(90deg, #E6E8F3 25%, #BFC6D7 50%, #E6E8F3 75%)',
                backgroundColor: '#BFC6D7',
              }}
            />
          ) : titleSlot ? (
            // Caller-supplied title content (e.g. VMS Requests/Emails selector).
            // The slot owner is responsible for any count rendering it wants.
            titleSlot
          ) : (
            <span style={{ fontWeight: 700, fontSize: 18, color: '#0F214B' }}>
              {stageConfig ? (language === 'fr' ? stageConfig.titleFrench : stageConfig.titleEnglish) : t(stage.toLowerCase() as any)}
              <span style={{ marginLeft: 8, fontWeight: 400, fontSize: 16, color: '#9795B5' }}>({totalRecords})</span>
            </span>
          )}
          {/* Refresh icon — sits right next to the title so it reads as
              "reload this view". Re-runs the current search query
              (filter / sort / page / facets unchanged); does NOT refetch
              configurations or views. Disabled + spinning while a refresh
              is in flight (isLoadingHeader covers both auto-loads and
              user-triggered reloads) so back-to-back clicks don't queue. */}
          {onRefresh && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={onRefresh}
                    disabled={isLoadingHeader}
                    aria-label={t('refresh') || 'Refresh'}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 9999,
                      border: '1px solid #E0E3EB',
                      background: '#fff',
                      color: '#0F214B',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: isLoadingHeader ? 'wait' : 'pointer',
                      opacity: isLoadingHeader ? 0.6 : 1,
                      padding: 0,
                    }}
                  >
                    <RotateCw
                      size={14}
                      strokeWidth={1.8}
                      className={isLoadingHeader ? 'animate-spin' : undefined}
                    />
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('refresh') || 'Refresh'}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Inline filter chip slot — populated by workflow-grid when a
              temporary OData filter (e.g. VmsEmail → linked VmsRequest
              "Showing: …") is active. Renders inside the title cluster so
              it stays visually tied to the totals it's narrowing. */}
          {filterIndicator}
        </div>
      )}
      
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {/* Column tools cluster: pin dock + jump-to-column. Both share the
              same square / dashed-border treatment so they read as a related pair. */}
          {showPinDock !== undefined && <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  role="button"
                  aria-label="Pin dock"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    border: '1px dashed #BFC6D7',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: showPinDock ? '#fff' : 'transparent',
                    opacity: showPinDock ? 1 : 0.5,
                    cursor: 'grab'
                  }}
                  onClick={() => onPinDockClick && onPinDockClick()}
                  onDragOver={(e) => { e.preventDefault(); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (onPinDockDrop) onPinDockDrop();
                  }}
                  draggable={showPinDock}
                  onDragStart={(e) => {
                    try {
                      e.dataTransfer.setData('application/x-workflow-pin', 'pin');
                    } catch {}
                  }}
                >
                  <Pin size={20} strokeWidth={1.5} className="text-[#0F214B]" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>{t('pinTooltip' as any)}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>}
          {onJumpToColumn && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    role="button"
                    tabIndex={0}
                    aria-label={t('jumpToColumn')}
                    onClick={onJumpToColumn}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        onJumpToColumn()
                      }
                    }}
                    style={{
                      width: 40,
                      height: 40,
                      borderRadius: 8,
                      border: '1px dashed #BFC6D7',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      cursor: 'pointer',
                      color: '#0F214B',
                    }}
                  >
                    <ScanSearch size={25} strokeWidth={1.2} />
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('jumpToColumn')} (Ctrl/⌘ + K)</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {/* Show-Completed toggle — rendered first in the right-side
              action group so it visually anchors with Edit columns / Filter.
              Gated by the stage config (only stages with showCompletedToggle
              opt in) and on the parent supplying the change handler so we
              don't render a no-op toggle if the host forgot to wire state. */}
          {stageConfig?.showCompletedToggle && onShowCompletedChange && (
            <div className="flex items-center gap-2 mr-1">
              <Switch
                checked={showCompleted}
                onCheckedChange={(checked) => onShowCompletedChange(Boolean(checked))}
                aria-label={t('showCompleted')}
                className="data-[state=checked]:bg-[#0F214B] focus-visible:ring-[#0F214B]"
              />
              <span className="text-sm text-gray-700 whitespace-nowrap">{t('showCompleted')}</span>
            </div>
          )}
          {leadingControls}
          {showEditColumnsButton && (
            <Button variant="outline" className="rounded-full border-[#BFC6D7] text-[#0F214B] font-normal px-5 py-2 flex items-center gap-2" onClick={onEditColumns}>
              <CustomColumnsIcon size={24} color="#0F214B" /> {t('editColumns')}
            </Button>
          )}
        {extraControls}
        {showFilterButton && (
          <Button
            variant="outline"
            className={`rounded-full border-[#BFC6D7] text-[#0F214B] font-normal px-5 py-2 flex items-center gap-2 relative ${isFilterActive ? 'bg-[rgba(15,33,75,0.2)]' : ''}`}
            style={isFilterActive ? { background: 'rgba(15,33,75,0.2)' } : {}}
            onClick={onToggleFilter}
          >
            <CustomFilterIcon size={24} color="#0F214B" />
            {t('filter')}
            {filterCount > 0 && (
              <span style={{ fontWeight: 500, fontSize: 14, color: '#23213D', marginLeft: 4 }}>
                ({filterCount})
              </span>
            )}
          </Button>
        )}
        {/* Export menu — icon-only button opening a dropdown of export
            formats. Trimmed to a circular icon (matches the VMS Settings
            cog style) instead of a labelled pill so the right-side action
            cluster stays compact. Only "Export as Excel" today; the
            dropdown shape is here so future formats (CSV, PDF report) can
            be added without re-shuffling the layout. Gated by
            showExportButton (wired from the ExportData authorized-action
            code in the workflow-grid host). */}
        {showExportButton && onExport && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center justify-center w-9 h-9 rounded-full border border-[#BFC6D7] text-[#0F214B] hover:bg-gray-100"
                aria-label={t('export') || 'Export'}
                title={t('export') || 'Export'}
              >
                <Download size={16} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem onClick={onExport} className="pl-2 pr-2">
                <Download size={14} className="mr-2 text-gray-600" />
                {t('exportToExcel')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
        {hasActiveFilters && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#BFC6D7',
                  fontWeight: 400,
                  fontSize: 16,
                  cursor: 'pointer',
                  marginLeft: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
                aria-label={t('clearFilters')}
              >
                × {t('clearFilters')}
                <ChevronDown size={14} style={{ opacity: 0.7 }} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              style={{
                minWidth: 200,
                background: '#fff',
                border: '1px solid #E0E3EB',
                borderRadius: 8,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
              }}
            >
              <DropdownMenuItem
                onClick={onClearFilters}
                style={{ fontSize: 14, color: '#2B345E', padding: '8px 12px', cursor: 'pointer' }}
              >
                {t('clearAllFilters')}
              </DropdownMenuItem>
              {!isDefaultView && onResetToCurrentView && currentViewName && (
                <DropdownMenuItem
                  onClick={onResetToCurrentView}
                  style={{ fontSize: 14, color: '#2B345E', padding: '8px 12px', cursor: 'pointer' }}
                >
                  {t('resetToView')} {currentViewName}
                </DropdownMenuItem>
              )}
              {onResetToDefault && (
                <DropdownMenuItem
                  onClick={onResetToDefault}
                  style={{ fontSize: 14, color: '#2B345E', padding: '8px 12px', cursor: 'pointer' }}
                >
                  {t('resetToDefault')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </div>
  )
})
